<template>
  <div v-if="survey && summary && summary.totalAnswered > 0 && result"
       class="survey-response-summary">

    <div class="survey-item">
      <div class="navigation">
        <ui-navigation :currentPage="index"
                       :lastPage="summary.totalAnswered"
                       @setPage="changeIndex" />
      </div>
      <h3>
        {{ $t('userResearch.survey.response.responseTime') }} :
        {{ responseDate }}
      </h3>
      <div class="items">
        <div v-for="(question, index) in survey.questions"
             :key="`question-${index}`"
             class="item">
          <div class="index">
            No. {{ index + 1 }}
          </div>
          <h4>
            {{ question.question }}
          </h4>
          <p v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX"
             class="description">
            {{ $t('userResearch.survey.detail.multiple') }}
          </p>
          <p v-if="!question.require"
             class="description">
            {{ $t('common.optional') }}
          </p>
          <div v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX"
               class="options">
            <ul class="option">
              <li v-for="(option, i) in Object.entries(question.answerOptions)"
                  :key="`option-${index}-${i}`">
                <ui-checkbox v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX"
                             :value="result[question.questionId] ? result[question.questionId].map(el => el.answerOption).includes(Number(option[0])) : false"
                             :disabled="result[question.questionId] ? !result[question.questionId].map(el => el.answerOption).includes(Number(option[0])) : true" />
                <ui-radio v-else
                          :value="result[question.questionId] ? result[question.questionId].answerOption === Number(option[0]) : false"
                          :disabled="result[question.questionId] ? result[question.questionId].answerOption !== Number(option[0]) : true" />
                {{ option[1] }}
              </li>
              <li v-if="question.answerOther"
                  class="other">
                <ui-checkbox v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX"
                             :value="!!otherText[question.questionId]"
                             :disabled="!otherText[question.questionId]" />
                <ui-radio v-else />
                <span class="other-text">{{ $t('userResearch.survey.create.other') }} :</span>
                <ui-input-text :type="'text'"
                               :modelValue="otherText[question.questionId] || ''"
                               :readonly="true"
                               :placeholder="' '"
                               :disabled="!otherText[question.questionId]" />
              </li>
            </ul>
          </div>
          <div v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT"
               class="options">
            <textarea :disabled="true"
                      :value="result[question.questionId] ? result[question.questionId].answerText : ''"></textarea>
          </div>
          <div v-if="question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE"
               class="options">
            <div class="preference">
              <div class="numbers">
                <div v-for="(el, number) in Array(question.answerOptions.maxValue - question.answerOptions.minValue + 1)"
                     :key="`option-${index}-${number}`"
                     class="number">
                  <div class="num-text">{{ number + question.answerOptions.minValue }}</div>
                  <ui-radio :value="result[question.questionId] ? result[question.questionId].answerOption === Number(number + question.answerOptions.minValue) : false"
                            :disabled="result[question.questionId] ? result[question.questionId].answerOption !== Number(number + question.answerOptions.minValue) : true" />
                </div>
              </div>
              <div v-if="question.answerOptions.minText"
                   class="description">
                {{ question.answerOptions.minText }}
              </div>
              <div v-if="question.answerOptions.maxText"
                   class="description">
                {{ question.answerOptions.maxText }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="navigation bottom">
        <ui-navigation :currentPage="index"
                       :lastPage="summary.totalAnswered"
                       @setPage="changeIndex" />
      </div>
    </div>

    <transition name="fade">
      <div v-if="loading"
           class="loader">
        <ba-loading :applyBackground="true"
                    :showLoadingText="true"/>
      </div>
    </transition>
  </div>
</template>

<script>
import { format, parse } from 'date-fns'
import { camelize } from '@/libs/utils'
import { getIndividualAnswer } from '@/api/survey'

import UiInputText from '@/components/ui/UiInputText'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiRadio from '@/components/ui/UiRadio'
import UiNavigation from '@/components/ui/UiNavigation'
import BaLoading from '@/components/ui/ba/Loading'
import { SURVEY_CONSTANT } from '@/libs/constants'

export default {
  name: 'SurveyResponseIndividual',
  components: {
    UiInputText,
    UiCheckbox,
    UiRadio,
    UiNavigation,
    BaLoading
  },
  created () {
    this.$emit('loaded', true)
    if (this.summary.totalAnswered > 0) {
      this.loadData()
    }
  },
  methods: {
    camelize (str) {
      return camelize(str)
    },
    loadData () {
      this.loading = true

      getIndividualAnswer({
        sid: this.$route.query.sid,
        surveyId: this.survey.surveyId,
        start: this.startDate,
        end: this.endDate,
        index: this.index
      }).then(result => {
        this.responseDate = format(parse(result[0].responseDate), this.$t('userResearch.survey.response.responseTimeFormat'))
        this.result = {}
        this.otherText = {}

        for (const ans of Object.values(result)) {
          if (ans.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX) {
            if (!this.result[ans.questionId]) {
              this.result[ans.questionId] = []
            }
            this.result[ans.questionId].push(ans)
          } else {
            this.result[ans.questionId] = ans
          }
          if (ans.answerOtherText) {
            this.otherText[ans.questionId] = ans.answerOtherText
          }
        }

        this.loading = false
      })
    },
    changeIndex (index) {
      this.index = index
      this.loadData()
    }
  },
  watch: {
    summary () {
      this.loadData()
    }
  },
  props: [
    'survey',
    'summary',
    'startDate',
    'endDate'
  ],
  data () {
    return {
      SURVEY_CONSTANT: SURVEY_CONSTANT,
      loading: false,
      index: 1,
      responseDate: null,
      result: null,
      otherText: null
    }
  }
}
</script>

<style lang="scss" scoped>
.survey-response-summary {
  .survey-item {
    h3 {
      margin: 75px 0 16px;
      font-size: 16px;
      font-weight: 500;
      color: #ec0047;
    }

    .navigation {
      float: right;
      margin: -10px -8px 0 0;

      &.bottom {
        margin-top: 15px;
      }

      .ui-navigation {
        padding-top: 0;
      }
    }

    table.item {
      background-color: #fff;
      width: 100%;
      border-collapse: collapse;

      th,
      td {
        border-top: 1px solid #d7d7d7;
        border-bottom: 1px solid #d7d7d7;
        text-align: left;
        padding: 18px;
        vertical-align: top;
      }

      .info-1 {
        font-size: 14px;
        font-weight: 500;
        color: #666;
        vertical-align: middle;
        padding-left: 30px;
        width: 132px;
      }

      .info-2 {
        font-size: 13px;
        color: #888;
        width: 117px;
      }

      td {
        padding: 12px;
        font-size: 15px;
        line-height: 1.6;
        color: #2f2f2f;

        p {
          margin-top: 10px;
          font-size: 13px;
          line-height: 1.62;
          color: #888;
        }

        ul {
          margin-top: 10px;
          font-size: 13px;
          line-height: 1.62;
          color: #888;

          li {
            position: relative;
            padding-left: 8px;

            &::before {
              content: "";
              position: absolute;
              top: 9px;
              left: 0;
              width: 3px;
              height: 3px;
              background-color: #444;
            }

            &:not(:first-child) {
              margin-top: 6px;
            }
          }
        }
      }
    }

    p.item {
      background-color: #fff;
      border-top: 1px solid #d7d7d7;
      border-bottom: 1px solid #d7d7d7;
      padding: 30px;
      font-size: 15px;
      line-height: 1.6;
      color: #2f2f2f;
    }

    div.items {
      border-bottom: 1px solid #d7d7d7;

      div.item {
        background-color: #fff;
        border-top: 1px solid #d7d7d7;
        padding: 40px 30px;

        .index {
          font-size: 22px;
          font-weight: 500;
          color: #2f2f2f;
          margin-bottom: 22px;
        }

        h4 {
          font-size: 18px;
          font-weight: 500;
          line-height: 1.28;
          color: #2f2f2f;
        }

        p.description {
          font-size: 12px;
          line-height: 1;
          color: #0e90a5;
          margin: 6px 0;
        }

        .options {
          padding-top: 14px;

          textarea {
            width: calc(100% - 26px);
            height: calc(157px - 18px);
            margin: 5px 0 4px;
            padding: 10px 12px;
            border-radius: 6px;
            border: solid 1px #d7d7d7;
            background-color: #e6e6e6;
            font-size: 14px;
            line-height: 1.57;
            color: #444;
            resize: none;
          }

          .option {
            li {
              margin: 11px 0 20px -10px;
              font-size: 14px;
              line-height: 1.36;
              color: #444;

              &:last-child {
                margin-bottom: 0;
              }

              &.other {
                margin-top: -17px;
                font-size: 14px;
                line-height: 1.36;
                color: #444;

                .other-text {
                  display: inline-block;
                  width: 48px;
                }

                .input--wrap {
                  width: 550px;
                  margin-top: -12px;
                }
              }
            }

            .ui_checkbox,
            .ui_radio {
              margin-left: 10px;
              margin-right: 2px;
            }

            .input--wrap {
              width: 784px;
            }
          }

          .preference {
            padding-bottom: 30px;

            &::after {
              content: "";
              display: block;
              clear: both;
            }

            .description {
              float: left;
              font-size: 14px;
              font-weight: bold;
              font-stretch: normal;
              font-style: normal;
              line-height: 1.36;
              letter-spacing: normal;
              color: #666;
              width: 120px;
              padding-top: 10px;
              padding-left: 30px;
              text-align: left;

              &:last-child {
                float: right;
                text-align: right;
                padding-right: 30px;
                padding-left: 0;
              }
            }

            .numbers {
              text-align: center;
              margin: 85px 0 0;
              display: table;
              width: 100%;

              .number {
                display: table-cell;
                vertical-align: top;
                margin-right: 35px;
                width: 18px;
                position: relative;
                text-align: center;

                .num-text {
                  position: absolute;
                  top: -27px;
                  left: 50%;
                  transform: translateX(-50%);
                  font-size: 14px;
                  font-weight: normal;
                  font-stretch: normal;
                  font-style: normal;
                  line-height: 1.36;
                  letter-spacing: normal;
                  color: #444;
                }

                .ui_radio {
                  display: inline-block;
                  z-index: 2;
                  margin-left: 0;
                  width: 17px;
                  cursor: default;
                  background-color: #fff;
                }

                &:not(:last-child) {
                  .num-text {
                    margin-right: 40px;
                  }

                  &::after {
                    position: absolute;
                    z-index: 1;
                    top: 6px;
                    left: 50%;
                    display: block;
                    content: "";
                    width: 100%;
                    border-bottom: 2px dashed #aaa;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .loader {
    position: fixed;
    z-index: 100;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100%;

    &.fade-enter-active, &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.2s ease;
    }

    &.fade-enter, &.fade-leave-to{
      opacity: 0;
    }
  }
}
</style>
